// PlayerDetailModal — the ficha individual: 6-stat breakdown, form/fatigue,
// weekly evolution (trend + sparkline + projection) and the sign/release
// action. Opened from RosterTab and TransferMarketTab rows.

import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useGameStore } from '@/store/gameStore'
import { backdropVariants, modalVariants, useMotionVariants } from '../motion/motionTokens'
import { ModalCloseButton } from './ModalCloseButton'
import { PlayerAvatar } from './PlayerAvatar'
import { StageBadge } from '../panels/RosterTab'
import { AngledButton } from './broadcast'
import {
  STAT_KEYS, STAT_LABELS, getEffectiveRating, type Player, type PlayerSnapshot,
} from '@/config/playersConfig'
import { getEvolution, TREND_LABELS } from '../helpers/playerEvolution'
import { playerPortraitSrc } from '../helpers/portraits'
import { TrendingUp, TrendingDown, Minus, HeartPulse, LineChart } from 'lucide-react'

function statColor(value: number): string {
  if (value >= 70) return 'var(--victory-orange)'
  if (value >= 50) return 'var(--gold)'
  return 'var(--text-secondary)'
}

function StatBar({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <span style={{ width: 110, fontSize: '0.72rem', color: 'var(--text-secondary)', flexShrink: 0 }}>
        {label}
      </span>
      <div className="progress-track" style={{ flex: 1 }}>
        <div
          className="progress-fill angled"
          style={{ width: `${value}%`, background: statColor(value) }}
        />
      </div>
      <span style={{ width: 26, textAlign: 'right', fontSize: '0.75rem', fontWeight: 700, color: statColor(value) }}>
        {value}
      </span>
    </div>
  )
}

/** Inline SVG sparkline of the level history (min-max normalized) */
function Sparkline({ history }: { history: PlayerSnapshot[] }) {
  const W = 140
  const H = 32
  const levels = history.map(s => s.level)
  const min = Math.min(...levels)
  const max = Math.max(...levels)
  const span = Math.max(1, max - min)
  const points = history.map((s, i) => {
    const x = history.length === 1 ? W : (i / (history.length - 1)) * W
    const y = H - 3 - ((s.level - min) / span) * (H - 6)
    return `${x.toFixed(1)},${y.toFixed(1)}`
  }).join(' ')

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      <polyline
        points={points}
        fill="none"
        stroke="var(--gold)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function EvolutionSection({ player }: { player: Player }) {
  const history = useGameStore(s => s.playerHistory[player.id])
  const evolution = getEvolution(history, player)
  if (!evolution) return null

  const TrendIcon = evolution.trend === 'progresando' ? TrendingUp
    : evolution.trend === 'en declive' ? TrendingDown : Minus
  const trendColor = evolution.trend === 'progresando' ? 'var(--padel-green)'
    : evolution.trend === 'en declive' ? 'var(--danger)' : 'var(--text-secondary)'

  return (
    <>
      <div className="card-title" style={{ fontSize: '0.8rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
        <LineChart size={13} /> Evolución
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <Sparkline history={evolution.history} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span className="pill" style={{ color: trendColor, background: 'var(--gold-fill)', width: 'fit-content' }}>
            <TrendIcon size={10} /> {TREND_LABELS[evolution.trend]}
            {evolution.levelDelta4w !== 0 && ` (${evolution.levelDelta4w > 0 ? '+' : ''}${evolution.levelDelta4w} en 4 sem.)`}
          </span>
          <span className="meta-text">
            Proyección: <strong style={{ color: 'var(--gold)' }}>{evolution.projectedPeak}</strong>
            {' / '}potencial {player.potential}
          </span>
        </div>
      </div>
    </>
  )
}

export interface PlayerDetailModalProps {
  player: Player | null
  mode: 'roster' | 'market'
  onClose: () => void
  /** "Fichar" (market) / "Rescindir" (roster); the parent runs the flow */
  onAction?: () => void
  actionDisabled?: boolean
}

export function PlayerDetailModal({ player, mode, onClose, onAction, actionDisabled }: PlayerDetailModalProps) {
  const modal = useMotionVariants(modalVariants)
  const backdrop = useMotionVariants(backdropVariants)

  return createPortal(
    <AnimatePresence>
      {player && (
        <>
          <motion.div
            variants={backdrop}
            initial="initial"
            animate="enter"
            exit="exit"
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 210 }}
          />
          <motion.div
            variants={modal}
            initial="initial"
            animate="enter"
            exit="exit"
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed', top: '50%', left: '50%', x: '-50%', y: '-50%',
              zIndex: 211,
              background: 'var(--bg-surface)',
              border: '1px solid color-mix(in srgb, var(--gold) 35%, transparent)',
              borderRadius: '16px',
              width: 'min(440px, 94vw)',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '1.3rem 1.4rem',
              boxShadow: 'var(--shadow-lg), inset 0 3px 0 0 var(--gold)',
            }}
          >
            <ModalCloseButton onClick={onClose} />

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.9rem' }}>
              <PlayerAvatar id={player.id} name={player.name} size={56} src={playerPortraitSrc(player)} />
              <div>
                <div style={{
                  fontFamily: 'var(--font-titles)', fontWeight: 800, fontSize: '1.05rem',
                  color: 'var(--text-strong)', display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap',
                }}>
                  {player.name}
                  <StageBadge age={player.age} />
                </div>
                <div className="meta-text" style={{ marginTop: '0.15rem' }}>
                  {player.age} años · Nivel <strong style={{ color: statColor(player.level) }}>{player.level}</strong>
                  {player.potential > player.level && <> / potencial {player.potential}</>}
                  {' · '}Rating {Math.round(getEffectiveRating(player))}
                </div>
              </div>
            </div>

            {player.injury && (
              <div style={{ marginBottom: '0.75rem' }}>
                <span className="pill pill-warning">
                  <HeartPulse size={10} /> {player.injury.name} · {Math.ceil(player.injury.daysRemaining)} días
                </span>
              </div>
            )}

            {/* Stats */}
            <div className="card-title" style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>
              Estadísticas
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1rem' }}>
              {STAT_KEYS.map(key => (
                <StatBar key={key} label={STAT_LABELS[key]} value={player.stats?.[key] ?? player.level} />
              ))}
            </div>

            {/* Evolution (roster players with history only) */}
            {mode === 'roster' && <EvolutionSection player={player} />}

            {/* Condition */}
            <div className="card-title" style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>
              Estado
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1rem' }}>
              <StatBar label="Forma" value={player.form} />
              <StatBar label="Fatiga" value={player.fatigue ?? 0} />
            </div>

            {/* Contract / record */}
            <div style={{
              display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.78rem',
              color: 'var(--text-secondary)', marginBottom: onAction ? '1.1rem' : 0,
            }}>
              <span>
                Salario: <span className="money-negative" style={{ fontWeight: 700 }}>
                  {player.salary.toLocaleString('es-ES')}€/sem
                </span>
              </span>
              {mode === 'market' ? (
                <span>
                  Fichaje: <span style={{ fontWeight: 700, color: 'var(--gold)' }}>
                    {player.signingFee.toLocaleString('es-ES')}€
                  </span>
                </span>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                  <TrendingUp size={12} />
                  {player.matchesWon}V / {player.matchesPlayed - player.matchesWon}D
                </span>
              )}
            </div>

            {onAction && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <AngledButton
                  tone={mode === 'market' ? 'gold' : 'danger'}
                  onClick={onAction}
                  disabled={actionDisabled}
                >
                  {mode === 'market' ? 'Fichar' : 'Rescindir contrato'}
                </AngledButton>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  )
}
